/**
 * T492 — OCR-Failure-Banner on the detail page.
 *
 * Renders above the preview when the OCR pipeline has failed or finished
 * with low confidence. Dismissable via a small "✕" button — the dismiss
 * is in-memory only (page reload re-evaluates the severity).
 */
import { useState } from 'react';
import type { OcrFailureSeverity } from './-ocrFailureSeverity';

interface OcrFailureBannerProps {
  severity: OcrFailureSeverity;
  /** Optional error message from expense_processing_status.errorMessage. */
  errorMessage?: string | null;
}

const COPY: Record<OcrFailureSeverity, { title: string; body: string; classes: string }> = {
  failed: {
    title: 'OCR-Erkennung fehlgeschlagen',
    body:
      'Die automatische Beleg-Erkennung ist abgebrochen. Bitte alle Felder manuell prüfen und ergänzen.',
    classes: 'bg-red-900/30 border border-red-700/50 text-red-200',
  },
  'low-confidence': {
    title: 'OCR-Erkennung unsicher',
    body:
      'Die Werte stammen aus der automatischen Erkennung mit niedriger Konfidenz (<40%). Bitte vor Freigabe prüfen.',
    classes: 'bg-amber-900/30 border border-amber-700/50 text-amber-200',
  },
};

export function OcrFailureBanner({ severity, errorMessage }: OcrFailureBannerProps) {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  const copy = COPY[severity];
  return (
    <div
      className={`rounded-lg px-4 py-3 flex items-start justify-between gap-4 ${copy.classes}`}
      data-testid="ocr-failure-banner"
      data-severity={severity}
      role="alert"
    >
      <div className="flex-1">
        <div className="font-medium">⚠ {copy.title}</div>
        <div className="text-sm mt-1">{copy.body}</div>
        {errorMessage && (
          <div className="text-xs mt-2 font-mono text-red-300/80">
            Pipeline-Fehler: {errorMessage}
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Banner ausblenden"
        data-testid="ocr-failure-banner-dismiss"
        className="text-current opacity-60 hover:opacity-100"
      >
        ✕
      </button>
    </div>
  );
}
